import type { OrderAttentionTone } from "@/lib/order-attention";

interface OrderAttentionCardProps {
  title: string;
  body: string;
  icon: string;
  tone: OrderAttentionTone;
  primaryLabel: string;
  primaryDestructive?: boolean;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  onPrimary: () => void;
  secondaryLabel?: string | null;
  onSecondary?: () => void;
  testId?: string;
}

const TONE_CLASS: Record<OrderAttentionTone, string> = {
  pending: "bg-amber-50 border-amber-200",
  warning: "bg-amber-50 border-amber-300",
  danger: "bg-orange-50 border-orange-200",
};

const ICON_CLASS: Record<OrderAttentionTone, string> = {
  pending: "text-amber-600",
  warning: "text-amber-700",
  danger: "text-orange-700",
};

export function OrderAttentionCard({
  title,
  body,
  icon,
  tone,
  primaryLabel,
  primaryDestructive = false,
  primaryDisabled = false,
  primaryLoading = false,
  onPrimary,
  secondaryLabel,
  onSecondary,
  testId,
}: OrderAttentionCardProps) {
  const titleId = `${testId ?? "order-attention"}-title`;
  return (
    <section
      data-testid={testId}
      role="region"
      aria-labelledby={titleId}
      className={`rounded-xl border p-4 sm:p-5 ${TONE_CLASS[tone]}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <i className={`${icon} text-xl mt-0.5 ${ICON_CLASS[tone]}`} aria-hidden="true" />
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0 sm:items-center">
          {secondaryLabel && onSecondary && (
            <button
              type="button"
              onClick={onSecondary}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {secondaryLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onPrimary}
            disabled={primaryDisabled || primaryLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap text-white disabled:opacity-60 ${
              primaryDestructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-amber-600 hover:bg-amber-700"
            }`}
          >
            {primaryLoading ? "Working..." : primaryLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
